import { z } from "zod"

import { stringInputSchema } from "@/shared/lib/validation-schemas"

/**
 * Validates a percentage string (0-100 with decimals allowed)
 */
const percentageDistributionString = ({
  requiredMessage,
  rangeMessage,
}: {
  requiredMessage: string
  rangeMessage: string
}) =>
  z
    .string()
    .trim()
    .min(1, { message: requiredMessage })
    .superRefine((value, ctx) => {
      const numeric = Number(value)

      if (!Number.isFinite(numeric) || numeric < 0 || numeric > 100) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: rangeMessage,
        })
      }
    })

/**
 * Valid options for hours open per day
 */
const validHoursOpenPerDayOptions = ["8", "9", "10", "12", "24"] as const

/**
 * Schema for validating weekly calculator form string input fields from the frontend.
 * Extends the base stringInputSchema with weekly-specific fields.
 * Note: period field omitted - calculator type determined by route.
 */
export const weeklyStringInputSchema = stringInputSchema
  .omit({ period: true })
  .extend({
    hoursOpenPerDay: z.enum(validHoursOpenPerDayOptions, {
      message: "form.errors.hoursOpenPerDay.invalid",
    }),

  weekStart: z.enum(["monday", "saturday", "sunday"], { message: "form.errors.weekStart.invalid" }),

  distributionMonday: percentageDistributionString({
    requiredMessage: "form.errors.distribution.required",
    rangeMessage: "form.errors.distribution.range",
  }),

  distributionTuesday: percentageDistributionString({
    requiredMessage: "form.errors.distribution.required",
    rangeMessage: "form.errors.distribution.range",
  }),

  distributionWednesday: percentageDistributionString({
    requiredMessage: "form.errors.distribution.required",
    rangeMessage: "form.errors.distribution.range",
  }),

  distributionThursday: percentageDistributionString({
    requiredMessage: "form.errors.distribution.required",
    rangeMessage: "form.errors.distribution.range",
  }),

  distributionFriday: percentageDistributionString({
    requiredMessage: "form.errors.distribution.required",
    rangeMessage: "form.errors.distribution.range",
  }),

  distributionSaturday: percentageDistributionString({
    requiredMessage: "form.errors.distribution.required",
    rangeMessage: "form.errors.distribution.range",
  }),

  distributionSunday: percentageDistributionString({
    requiredMessage: "form.errors.distribution.required",
    rangeMessage: "form.errors.distribution.range",
  }),
})

/**
 * Schema for the complete weekly calculator form with custom refinement
 * to validate that the sum of all day distributions equals 100%
 */
export const weeklyFormSchema = weeklyStringInputSchema.superRefine((values, ctx) => {
  const dayDistributionFields = [
    "distributionMonday",
    "distributionTuesday",
    "distributionWednesday",
    "distributionThursday",
    "distributionFriday",
    "distributionSaturday",
    "distributionSunday",
  ] as const

  const total = dayDistributionFields.reduce((sum, key) => {
    return sum + (Number(values[key] || 0) || 0)
  }, 0)

  // Allow small floating point differences (tolerance of 0.01%)
  if (Math.abs(total - 100) > 0.01) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "form.errors.distribution.totalNot100",
      path: ["distributionSunday"],
    })
  }
})

export type WeeklyStringInputData = z.infer<typeof weeklyFormSchema>
